import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnInit,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ParamMap, Router, RouterLink } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { PARTNER_DEAL_REALTIME } from '@features/dashboard/commercial/services/partner-deals-realtime.service';
import { RealtimePresenceBannerComponent } from '@shared/components/realtime-presence-banner/realtime-presence-banner.component';
import {
  PARTNER_DEAL_INTERNAL_STAGES,
  PARTNER_DEAL_STAGE_LABELS,
  PARTNER_VIEW_STAGE_LABELS,
  PartnerDealResponse,
  PartnerDealStage,
  partnerDealStagesFor,
  toPartnerVisibleStage,
} from '@interfaces/partners/responses/partner-deal.response';
import {
  PARTNER_DEAL_REJECTION_REASON_LABELS,
  PartnerDealTimelineEntryResponse,
  PartnerDealTimelineResponse,
} from '@interfaces/partners/responses/partner-deal-timeline.response';
import {
  COMMERCIAL_CLIENT_TERM_ORIGIN_LABELS,
  COMMERCIAL_CLIENT_TERM_STATUS_LABELS,
  COMMERCIAL_CLIENT_TERM_STATUS_SEVERITY,
  CommercialClientTermResponse,
  CommercialClientTermSignatoryResponse,
} from '@interfaces/partners/responses/commercial-client-term.response';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { ModalTermSignatoriesComponent } from './components/modal-term-signatories/modal-term-signatories.component';
import { VaultMaskCnpjPipe } from '@shared/pipes/vault-mask-cnpj.pipe';
import { FilePreviewService } from '@shared/services/file-preview.service';
import { Subscription, catchError, combineLatest, finalize, first, forkJoin, map, of } from 'rxjs';
import { ErrorResponse } from '@models/commom/error-response';
import {
  DealDecisionMode,
  ModalDealDecisionComponent,
} from './components/modal-deal-decision/modal-deal-decision.component';
import { CorporateRegistryCacheService } from '@services/http/corporate-registry/corporate-registry-cache.service';
import { DealRegistryPanelComponent } from './components/registry/deal-registry-panel/deal-registry-panel.component';
import { DealDataRoomTabComponent } from './components/deal-data-room-tab/deal-data-room-tab.component';
import { DealGatesPanelComponent } from './components/deal-gates-panel/deal-gates-panel.component';
import { DealCommitteePanelComponent } from './components/deal-committee-panel/deal-committee-panel.component';
import { DealCommitteeAsideComponent } from './components/deal-committee-aside/deal-committee-aside.component';
import { ModalCommitteeSubmitComponent } from './components/modal-committee-submit/modal-committee-submit.component';
import {
  isCommitteeDenial,
  resolveCommitteeNextStep,
  shouldOfferCommitteeSubmit,
} from './components/deal-committee-panel/deal-committee-presenter';
import { PartnerDealCommitteeViewResponse } from '@interfaces/partners/responses/partner-deal-committee.response';
import { DealMeetingsPanelComponent } from './components/deal-meetings-panel/deal-meetings-panel.component';
import { DealQuotesPanelComponent } from './components/deal-quotes-panel/deal-quotes-panel.component';
import { DealEconomicAnalysisTabComponent } from './components/deal-unscoped-tabs/deal-economic-analysis-tab.component';
import { DealPartnerEconomicAnalysisTabComponent } from './components/deal-unscoped-tabs/deal-partner-economic-analysis-tab.component';
import { Permissions } from '@app/core/guard/Permissions';
import { PermissionsService } from '@services/helpers/permissions.service';
import {
  DealTab,
  canSeeBriefing,
  canSeeEconomicAnalysis,
  canSeeKyc,
  resolveShownTab,
} from './deal-tabs';
import { DealBriefingTabComponent } from './components/deal-briefing-tab/deal-briefing-tab.component';
import { ConsentNotice, consentNotice } from './deal-consent-notice';
import { DealConsentCardComponent } from './components/deal-consent-card/deal-consent-card.component';
import { DealKycTabComponent } from './components/deal-kyc-tab/deal-kyc-tab.component';
import { DealGatesSummaryComponent } from './components/deal-gates-summary/deal-gates-summary.component';
import { ClientAccessCardComponent } from './components/client-access-card/client-access-card.component';
import {
  DealRegistrySummary,
  DealRegistrySummaryComponent,
} from './components/deal-registry-summary/deal-registry-summary.component';
import { CorporateRegistryHttpService } from '@services/http/corporate-registry/corporate-registry-http.service';
import { EmpresaCadastralResponse } from '@interfaces/corporate-registry/responses/empresa-cadastral.response';
import { DealUrlState, readDealUrlState, toDealUrlParams } from './deal-url-state';
import { DealBankJourneyStore } from './components/bank-journey/deal-bank-journey.store';
import { DealBankJourneyTabComponent } from './components/bank-journey/deal-bank-journey-tab/deal-bank-journey-tab.component';
import { DealBankJourneyCardComponent } from './components/bank-journey/deal-bank-journey-card/deal-bank-journey-card.component';
import { DealBankJourneySummaryComponent } from './components/bank-journey/deal-bank-journey-summary/deal-bank-journey-summary.component';
import { DealJourneySwitchComponent } from './components/bank-journey/deal-journey-switch/deal-journey-switch.component';
import { BankJourneyCommentsDrawerComponent } from './components/bank-journey/bank-journey-comments-drawer/bank-journey-comments-drawer.component';
import {
  conteudoDaEtapa as conteudoDaEtapaDoFunil,
  descreverEvento as descreverEventoDoDeal,
  formatarDuracao as formatarDuracaoDeEtapa,
  iconeDoEvento as iconeDoEventoDoDeal,
  ProximoPasso,
  resolverDependeDeVoce,
  resolverOrigemDoDeal,
  resolveDealTerm,
  DealTermSummary,
  resolverProximaEtapa,
  resolverProximoPasso,
  resolverSituacao,
  SituacaoDoDeal,
} from './deal-presenter';

/** Uma etapa do stepper, já resolvida contra a etapa atual do deal. */
interface StepperStage {
  stage: PartnerDealStage;
  label: string;
  done: boolean;
  /** Pulada ao mover o deal e ainda pendente: o relógio no stepper (card 3189). */
  skipped: boolean;
  current: boolean;
  /** Quando o deal saiu da etapa pela última vez, se houver registro. */
  completedAt?: string | null;
}

/**
 * Tela interna de um deal (card 2952, bloco B).
 *
 * É o palco do módulo: dela saem a solicitação de assinatura do NDA, o acompanhamento e a linha
 * do tempo. Chega-se aqui pelo board e pelo **botão do e-mail de pré-aprovação**.
 *
 * ⚠️ **Entrega parcial, e isso é combinado.** As abas Parecer &amp; garantias e Cotações, mais os blocos de Gates de elegibilidade e Comissão, são **card próprio** e
 * não aparecem aqui. Exibi-las vazias seria pior do que não exibi-las: o parceiro leria como
 * defeito.
 */
@Component({
  selector: 'app-deal-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    SharedModule,
    VaultMaskCnpjPipe,
    ModalTermSignatoriesComponent,
    ModalDealDecisionComponent,
    DealRegistryPanelComponent,
    DealDataRoomTabComponent,
    DealGatesPanelComponent,
    DealCommitteePanelComponent,
    DealCommitteeAsideComponent,
    ModalCommitteeSubmitComponent,
    DealMeetingsPanelComponent,
    DealQuotesPanelComponent,
    DealEconomicAnalysisTabComponent,
    DealPartnerEconomicAnalysisTabComponent,
    DealKycTabComponent,
    DealBriefingTabComponent,
    DealConsentCardComponent,
    DealGatesSummaryComponent,
    DealRegistrySummaryComponent,
    ClientAccessCardComponent,
    RealtimePresenceBannerComponent,
    DealBankJourneyTabComponent,
    DealBankJourneyCardComponent,
    DealBankJourneySummaryComponent,
    DealJourneySwitchComponent,
    BankJourneyCommentsDrawerComponent,
  ],
  // O cache do dossiê da Receita vive com a TELA: trocar de etapa destrói o painel do Registro,
  // e voltar a ele não pode reconsultar a Receita (card 2986). A Jornada nos bancos (card 3707) também:
  // a aba Bancos, o cartão da Visão geral e o painel de comentários leem o mesmo store.
  providers: [CorporateRegistryCacheService, DealBankJourneyStore],
  templateUrl: './deal-detail.component.html',
  styleUrl: './deal-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DealDetailComponent extends VaultBaseComponent implements OnInit {
  protected readonly deal = signal<PartnerDealResponse | null>(null);
  protected readonly timeline = signal<PartnerDealTimelineResponse | null>(null);
  /**
   * A linha do tempo não carregou (card 3189). Sem este estado, a falha virava "Sem histórico
   * ainda" — e quem olhava concluía que o histórico do deal tinha sido apagado.
   */
  protected readonly timelineFalhou = signal(false);
  protected readonly term = signal<CommercialClientTermResponse | null>(null);
  /**
   * Cadastro da empresa na Receita, para a aba KYC (card 3380). Carregado só para o time interno, pelo
   * mesmo cache do painel da etapa Registro (chave `cnpj|empresa`): a Receita é consultada uma vez só.
   */
  protected readonly kycCompany = signal<EmpresaCadastralResponse | null>(null);
  /**
   * Resumo cadastral (consultas VAAS) da Visão geral (card 3380, task 3383). Sempre `null` por enquanto: a
   * consulta à VAAS ainda não existe no backend (card #3075). Com `null` o cartão não aparece.
   */
  protected readonly registrySummary = signal<DealRegistrySummary | null>(null);
  protected readonly activeTab = signal<DealTab>('overview');
  protected readonly notFound = signal(false);

  /**
   * Etapa que a pessoa escolheu rever no stepper; `null` = a etapa atual do deal.
   *
   * Cada etapa tem o seu conteúdo na Visão geral — Registro mostra quem é a empresa na Receita,
   * Consentimento mostra o termo — e um deal adiantado precisa conseguir voltar a olhar os dois.
   * A coluna lateral NÃO acompanha: ela sempre fala do que fazer agora.
   */
  protected readonly selectedStage = signal<PartnerDealStage | null>(null);

  /**
   * Trilha da tela, no mesmo componente que as telas irmãs usam (`app-breadcrumb`).
   *
   * Nasceu como um `<nav>` próprio, e isso era divergência sem motivo: `my-deals`,
   * `register-deal` e `partner-users` — as vizinhas do mesmo módulo — usam o componente do
   * design system, com o ícone de home e o separador dele.
   */
  protected breadcrumbs: MenuItem[] = [
    { label: 'Comercial' },
    { label: 'Meus deals', routerLink: '/dashboard/commercial/my-deals' },
  ];

  protected readonly home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
  };

  protected readonly reasonLabels = PARTNER_DEAL_REJECTION_REASON_LABELS;
  protected readonly termStatusLabels = COMMERCIAL_CLIENT_TERM_STATUS_LABELS;
  protected readonly termStatusSeverity = COMMERCIAL_CLIENT_TERM_STATUS_SEVERITY;
  protected readonly termOriginLabels = COMMERCIAL_CLIENT_TERM_ORIGIN_LABELS;

  @ViewChild('modalAssinantes') modalAssinantes: ModalTermSignatoriesComponent | null = null;
  @ViewChild('modalDecisao') modalDecisao: ModalDealDecisionComponent | null = null;
  @ViewChild('modalCommitteeSubmit') modalCommitteeSubmit: ModalCommitteeSubmitComponent | null =
    null;

  /**
   * Comitê de crédito (card 3789), como o painel da etapa Comitê o carregou: alimenta a lateral
   * (prazo, regras) e o cartão "próximo passo". A lógica mora no painel e no presenter dele.
   */
  protected readonly committeeView = signal<PartnerDealCommitteeViewResponse | null>(null);
  /** Muda a cada releitura do tempo real, para o painel do comitê reler junto. */
  protected readonly committeeReloadKey = signal(0);

  /** Assinante sendo removido ou cobrado — trava só o botão dele, não a tela toda. */
  protected readonly acaoEmCurso = signal<string | null>(null);

  private dealId = '';
  /** Leitura do deal em voo (`loadDeal`); a próxima cancela esta. */
  private loadDealSubscription: Subscription | null = null;
  /** Tempo real (card 3496): tópico, textos e eventos do banner de presença. */
  protected readonly partnerDealRealtime = PARTNER_DEAL_REALTIME;
  private lastParamMap: ParamMap | null = null;
  private readonly router = inject(Router);

  constructor(
    translateService: TranslateService,
    private readonly route: ActivatedRoute,
    private readonly partnersHttpService: PartnersHttpService,
    private readonly dateFormatterService: DateFormatterService,
    private readonly toastHelperService: ToastHelperService,
    private readonly utilsService: UtilsService,
    private readonly confirmationService: ConfirmationService,
    private readonly filePreviewService: FilePreviewService,
    private readonly cdr: ChangeDetectorRef
  ) {
    super(translateService);
  }

  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  /**
   * Id do deal carregado: muda na primeira carga e na troca de deal, não a cada releitura pelo tempo real (o
   * `computed` só avisa quando o valor muda). Assim a faixa não pula de volta para a aba ativa enquanto a pessoa rola.
   */
  private readonly loadedDealId = computed(() => this.deal()?.id ?? null);

  /**
   * Card 3878: a faixa de abas rola na horizontal quando falta espaço (chat encostado, janela estreita). Ao trocar de
   * aba, ou quando o deal carrega com `?tab=briefing` (a última), a aba ativa é trazida para a vista rolando só a
   * faixa, nunca a página: `scrollIntoView` puxaria a tela inteira numa releitura pelo tempo real.
   */
  private readonly keepActiveTabVisible = effect(() => {
    this.activeTab();
    this.loadedDealId();
    afterNextRender(
      () => {
        const tab = this.host.nativeElement.querySelector<HTMLElement>('.tabs__tab--active');
        const bar = tab?.parentElement;
        if (!tab || !bar || bar.scrollWidth <= bar.clientWidth) return;
        const tabBox = tab.getBoundingClientRect();
        const barBox = bar.getBoundingClientRect();
        if (tabBox.right > barBox.right) bar.scrollLeft += tabBox.right - barBox.right;
        else if (tabBox.left < barBox.left) bar.scrollLeft -= barBox.left - tabBox.left;
      },
      { injector: this.injector }
    );
  });

  /** Jornada nos bancos do deal (card 3707): aba Bancos, cartão da Visão geral e comentários. */
  protected readonly bankJourney = inject(DealBankJourneyStore);
  private readonly registryCache = inject(CorporateRegistryCacheService);
  private readonly registryHttp = inject(CorporateRegistryHttpService);

  /**
   * `COMMERCIAL_INVITE_CLIENT` (card 3461): card "Acesso do cliente" na aba KYC (interno) e na Visão
   * geral (parceiro). O card carrega e desenha sozinho; aqui só se decide se ele aparece.
   */
  protected readonly canInviteClient = toSignal(
    inject(PermissionsService).hasPermission(Permissions.Commercial.INVITE_CLIENT),
    { initialValue: false }
  );

  /** `PARTNERDEALCOMMITTEE_VIEW` (card 3789): painel e lateral do comitê; sem ela, o servidor responde 403. */
  protected readonly canViewCommittee = toSignal(
    inject(PermissionsService).hasPermission(Permissions.PartnerDealCommittee.VIEW),
    { initialValue: false }
  );

  /** `PARTNERDEALCOMMITTEE_SUBMIT` (card 3789): o próximo passo de Análise vira "Enviar ao comitê". */
  private readonly canSubmitToCommittee = toSignal(
    inject(PermissionsService).hasPermission(Permissions.PartnerDealCommittee.SUBMIT),
    { initialValue: false }
  );

  /** `PARTNERDEALBRIEFING_VIEW` (card 3878): aba Briefing; sem ela, o servidor responde 403. */
  private readonly canViewBriefing = toSignal(
    inject(PermissionsService).hasPermission(Permissions.PartnerDealBriefing.VIEW),
    { initialValue: false }
  );

  /** `DFP_VIEW`, a permissão da Análise Econômica: sem ela a aba embutida nasceria vazia. */
  private readonly hasDfpPermission = toSignal(
    inject(PermissionsService).hasPermission(Permissions.Dfp.VIEW),
    { initialValue: false }
  );

  ngOnInit(): void {
    // paramMap, e não snapshot (card 3030): o aviso do sino navega de um deal para outro, e o
    // router REAPROVEITA este componente na mesma rota — com snapshot a URL trocava e a tela
    // continuava mostrando o deal anterior.
    //
    // Etapa e aba vêm da query string (card 3307, T06), no MESMO fluxo do id: o sino pode trazer
    // `?tab=documents` de outro deal, e o reset da troca de deal não pode apagar o que a URL pediu.
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([params, query]) => {
        const id = params.get('id') ?? '';
        // Só a troca de rota recarrega: trocar de aba re-emite o MESMO `paramMap`.
        const routeChanged = params !== this.lastParamMap;
        this.lastParamMap = params;
        if (routeChanged && (id !== this.dealId || !this.deal())) {
          this.dealId = id;
          this.reiniciarEstado();
          this.bankJourney.setDeal(this.dealId || null);

          if (!this.dealId) {
            this.notFound.set(true);
            return;
          }

          this.loadDeal();
        }

        const { stage, tab } = readDealUrlState(query);
        this.selectedStage.set(stage);
        this.activeTab.set(tab);
      });
  }

  /**
   * Grava etapa e aba na URL; quem aplica na tela é a assinatura do `ngOnInit`. Entra no histórico
   * (o voltar do navegador desfaz a troca), exceto quando é a própria tela que corrige a etapa.
   */
  private updateUrlState(state: DealUrlState, replaceUrl = false): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: toDealUrlParams(state),
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }

  /**
   * Zera o que pertence ao deal anterior ao trocar de deal na mesma tela. Voltar para a Visão geral
   * também destrói o onboarding embutido da aba Documentos, que lê a empresa uma vez só.
   */
  private reiniciarEstado(): void {
    this.deal.set(null);
    this.timeline.set(null);
    this.timelineFalhou.set(false);
    this.term.set(null);
    this.notFound.set(false);
    this.selectedStage.set(null);
    this.activeTab.set('overview');
    this.committeeView.set(null);
  }

  /**
   * Recarrega só a linha do tempo, depois de uma ação que grava evento nela (convite do cliente).
   * Falha aqui não apaga a linha do tempo que já está na tela.
   */
  protected reloadTimeline(): void {
    this.partnersHttpService
      .getDealTimeline(this.dealId)
      .pipe(
        first(),
        catchError(() => of(null))
      )
      .subscribe(timeline => {
        if (!timeline) return;
        this.timeline.set(this.projetarLinhaDoTempoParaQuemVe(timeline, this.deal()));
        this.timelineFalhou.set(false);
        this.cdr.markForCheck();
      });
  }

  /**
   * Carrega o deal e a linha do tempo.
   *
   * O deal vem da listagem porque não há endpoint de um só — e a listagem já é recortada pelo
   * backend, então um id que não aparece aqui é um deal que este usuário não pode ver. É a mesma
   * resposta para "não existe" e "não é seu", de propósito.
   */
  private loadDeal(silent = false): void {
    // Uma leitura nova cancela a anterior (card 3496): releituras do tempo real e troca de deal
    // nunca deixam uma resposta mais velha sobrescrever a mais nova.
    this.loadDealSubscription?.unsubscribe();

    if (!silent) {
      this.utilsService.setIsLoading(true, 'Carregando o deal...');
    }

    // 🐞 A linha do tempo e o termo são SECUNDÁRIOS, e cada um entra com o seu próprio
    // `catchError`. Sem isso o `forkJoin` derruba a resposta inteira quando um deles falha, o
    // `error` marca `notFound` e a tela anuncia "Deal não encontrado" — sobre um deal que existe
    // e que o usuário pode ver. Aconteceu em 24/09/2026, quando uma coluna nova ainda não estava
    // no banco: a consulta do termo quebrava e a tela culpava o deal.
    this.loadDealSubscription = forkJoin({
      deals: this.partnersHttpService.getMyDeals(),
      timeline: this.partnersHttpService
        .getDealTimeline(this.dealId)
        .pipe(catchError(() => of(null as PartnerDealTimelineResponse | null))),
      term: this.partnersHttpService.getDealTerm(this.dealId).pipe(catchError(() => of(null))),
    })
      .pipe(
        first(),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (!silent) {
            this.utilsService.setIsLoading(false);
          }
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: ({ deals, timeline, term }) => {
          const encontrado = this.projetarDealParaQuemVe(
            (deals ?? []).find(d => d.id === this.dealId) ?? null
          );
          this.deal.set(encontrado);
          this.notFound.set(!encontrado);

          if (encontrado) {
            this.breadcrumbs = [
              { label: 'Comercial' },
              { label: 'Meus deals', routerLink: '/dashboard/commercial/my-deals' },
              { label: encontrado.corporateName },
            ];
          }

          this.timeline.set(this.projetarLinhaDoTempoParaQuemVe(timeline, encontrado));
          this.timelineFalhou.set(timeline === null);
          // `null` aqui é o caso normal de "ainda não solicitado", e não uma falha.
          this.term.set(term);
          this.loadKycCompany(encontrado);
        },
        error: () => {
          // Releitura do tempo real que falhou: a tela continua com o que já mostrava.
          if (silent) return;
          this.notFound.set(true);
          this.toastHelperService.showToastError('Não foi possível carregar o deal.');
        },
      });
  }

  /**
   * Tempo real (card 3496): outra pessoa mudou o deal. `silent` = releitura sem a tela de
   * carregamento (o parceiro, que só relê); sem ele, Recarregar pedido pelo aviso.
   */
  protected reloadFromRealtime(silent: boolean): void {
    if (this.dealId) {
      this.loadDeal(silent);
      this.committeeReloadKey.update(key => key + 1);
    }
  }

  /**
   * Termo que existe **no provedor** — e não apenas como linha no nosso banco.
   *
   * ⚠️ `Pendente` é "ainda não enviado": esse status só vive entre gravar a linha e abrir o
   * processo no Clicksign. Quando a abertura falha, é ali que o termo para — e oferecer
   * "acrescentar signatário" nesse ponto avisaria que vai cancelar um documento inexistente.
   */
  protected get termoEmAndamento(): boolean {
    const status = this.term()?.status;
    return !!status && status !== 'Pendente' && status !== 'Cancelado';
  }

  // ─────────────────────────── decisão da Legatus (bloco A) ───────────────────────────

  /**
   * Se as ações de decisão aparecem.
   *
   * O flag vem **calculado do servidor** (`canDecide`), e não de um `hasRole` aqui: a régua é
   * "usuário interno, e nunca quem tem parceiro", e reimplementá-la na tela criaria uma segunda
   * fonte para a mesma pergunta. Esconder o botão é conforto — os endpoints revalidam.
   */
  protected get podeDecidir(): boolean {
    return this.deal()?.canDecide === true;
  }

  /** Pré-aprovar só faz sentido no começo do funil e em deal que não foi rejeitado. */
  protected get podePreAprovar(): boolean {
    const deal = this.deal();
    return this.podeDecidir && !!deal && !deal.rejectedAt && deal.stage === 'Registro';
  }

  /** Rejeitar vale em qualquer etapa, desde que ainda não esteja rejeitado. */
  protected get podeRejeitar(): boolean {
    const deal = this.deal();
    return this.podeDecidir && !!deal && !deal.rejectedAt;
  }

  protected get podeReabrir(): boolean {
    const deal = this.deal();
    return this.podeDecidir && !!deal && !!deal.rejectedAt;
  }

  /**
   * A rejeição que está de pé: motivo, observação e quando.
   *
   * O deal carrega só o `rejectedAt` — **o motivo mora na linha do tempo**, no evento `Rejected`.
   * É de propósito: um deal pode ser rejeitado, reaberto e rejeitado de novo, e um campo na
   * entidade apagaria a explicação anterior a cada vez. Por isso a busca é de trás para frente
   * (as entradas chegam em ordem crescente): a **última** rejeição é a que corresponde ao
   * `rejectedAt` vigente.
   *
   * ⚠️ Pode voltar `null` com o deal rejeitado — deal rejeitado antes da linha do tempo existir
   * não tem o evento. Quem consome precisa mostrar a rejeição mesmo sem o motivo, em vez de
   * esconder o aviso inteiro.
   */
  protected get rejeicaoAtual(): { motivo: string | null; observacao: string | null } | null {
    if (!this.deal()?.rejectedAt) return null;

    const entrada = (this.timeline()?.entries ?? [])
      .slice()
      .reverse()
      .find(e => e.eventType === 'Rejected');

    if (!entrada) return null;

    return {
      motivo: entrada.rejectionReason ? this.reasonLabels[entrada.rejectionReason] : null,
      observacao: entrada.notes?.trim() || null,
    };
  }

  /**
   * Quem pode mover o deal de etapa: **só o time interno**.
   *
   * A etapa é a leitura que a Legatus faz do funil — se o parceiro pudesse movê-la, declararia
   * sozinho que o próprio deal está em comitê ou em contratação. Ele acompanha; quem conduz move.
   * `canDecide` vem do servidor, e é o mesmo flag de pré-aprovar/rejeitar: uma segunda régua
   * reimplementada aqui um dia discordaria do endpoint.
   *
   * ⚠️ **Registro é exceção para todo mundo, inclusive para a Legatus**: dali o deal sai pela
   * **pré-aprovação** (25/09/2026), não por "avançar etapa". Pré-aprovar registra a decisão, avisa
   * o parceiro por e-mail e escolhe o destino — pula o Consentimento quando o cliente já assinou o
   * termo. Um botão de avançar ao lado faria o mesmo trajeto sem nada disso. Quem cuida dessa
   * exceção é o {@link proximoPasso}, que trata Registro antes de chegar na ação de avançar.
   */
  protected get podeAvancarEtapa(): boolean {
    return this.podeDecidir;
  }

  /**
   * Pré-aprova o deal, com confirmação simples.
   *
   * Sem modal de propósito: não há motivo nem justificativa a preencher, e um formulário vazio
   * seria um passo a mais sem conteúdo. Mas também não é um clique seco — a ação dispara e-mail
   * para o parceiro e move o deal de etapa.
   */
  protected preAprovar(): void {
    const deal = this.deal();
    if (!deal || !this.podePreAprovar) return;

    this.confirmationService
      .confirm(
        'Pré-aprovar este deal?',
        `${deal.corporateName} avança no funil e o parceiro é avisado por e-mail.`
      )
      .pipe(first())
      .subscribe(confirmado => {
        if (!confirmado) return;

        this.acaoEmCurso.set('pre-aprovar');
        this.cdr.markForCheck();

        this.partnersHttpService
          .preApproveDeal(deal.id)
          .pipe(
            first(),
            finalize(() => {
              this.acaoEmCurso.set(null);
              this.cdr.markForCheck();
            })
          )
          .subscribe({
            next: () => {
              this.toastHelperService.showToastSuccess('Deal pré-aprovado.');
              this.loadDeal();
            },
            error: erro => this.avisarFalha('Não foi possível pré-aprovar', erro),
          });
      });
  }

  /** Abre a modal de rejeição ou de reabertura. */
  protected abrirDecisao(modo: DealDecisionMode): void {
    const deal = this.deal();
    if (!deal) return;

    this.modalDecisao?.abrir(deal, modo, isCommitteeDenial(this.timeline()?.entries));
  }

  /** A modal decidiu: o deal, a etapa e a linha do tempo mudaram juntos. */
  protected decisaoTomada(): void {
    this.loadDeal();
  }

  /**
   * Abre o documento do termo **na própria tela**, no visualizador compartilhado.
   *
   * Usa o `FilePreviewService`, que é o caminho indicado para consumidor novo (ver o README de
   * `shared/components/file-preview-modal`): ele encapsula a configuração do `DynamicDialog`
   * — 95vw×95vh, `styleClass` obrigatório, `baseZIndex` da faixa de modais — que já estava
   * copiada em três lugares, e uma cópia divergente quebra o layout de um jeito difícil de achar.
   *
   * ⚠️ O visualizador busca o PDF e o renderiza como `blob:` num iframe. Isso **depende de CORS**
   * no storage; quando o fetch falha, a própria modal cai num fallback de download em vez de
   * travar carregando. O Data Room e o Onboarding já consomem o mesmo storage por este caminho.
   */
  protected verTermo(): void {
    if (this.acaoEmCurso()) return;

    this.acaoEmCurso.set('ver-termo');
    this.cdr.markForCheck();

    this.partnersHttpService
      .getDealTermDocument(this.dealId)
      .pipe(
        first(),
        finalize(() => {
          this.acaoEmCurso.set(null);
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: documento => {
          if (!documento?.url) {
            this.toastHelperService.showToastError('O documento deste termo não está disponível.');
            return;
          }

          // `open` devolve false quando o tipo não tem viewer. Não deve acontecer — é sempre PDF
          // —, mas cair para a aba nova é melhor que o clique não fazer nada.
          const abriu = this.filePreviewService.open(documento.fileName, documento.url);
          if (!abriu) window.open(documento.url, '_blank', 'noopener,noreferrer');
        },
        error: erro => this.avisarFalha('Não foi possível abrir o termo', erro),
      });
  }

  /** Abre a modal de assinantes — serve para o primeiro envio e para acrescentar depois. */
  protected abrirModalDeAssinantes(): void {
    const deal = this.deal();
    if (!deal) return;

    this.modalAssinantes?.abrir(deal, this.term());
  }

  /**
   * Remove um assinante do cliente.
   *
   * ⚠️ Isto **cancela o documento no provedor e abre outro, em branco**: quem já assinou assina
   * de novo. A confirmação diz isso com todas as letras — é consequência do desenho do Clicksign,
   * não uma escolha nossa, e quem clica precisa saber antes.
   */
  protected removerAssinante(assinante: CommercialClientTermSignatoryResponse): void {
    const jaAssinou = (this.term()?.signatories ?? []).some(s => !!s.signedAt);

    this.confirmationService
      .confirm(
        `Remover ${assinante.name}?`,
        jaAssinou
          ? 'O documento atual será cancelado e um novo será enviado. As assinaturas já ' +
              'coletadas serão perdidas, e todos precisarão assinar de novo.'
          : 'O documento atual será cancelado e um novo será enviado aos demais assinantes.'
      )
      .pipe(first())
      .subscribe(confirmado => {
        if (!confirmado) return;

        this.acaoEmCurso.set(assinante.id);
        this.cdr.markForCheck();

        this.partnersHttpService
          .removeDealTermSignatory(this.dealId, assinante.id)
          .pipe(
            first(),
            finalize(() => {
              this.acaoEmCurso.set(null);
              this.cdr.markForCheck();
            })
          )
          .subscribe({
            next: termo => {
              this.term.set(termo);
              this.toastHelperService.showToastSuccess('Assinante removido', '');
            },
            error: erro => this.avisarFalha('Não foi possível remover o assinante', erro),
          });
      });
  }

  /** Reenvia por e-mail a cobrança de assinatura a quem ainda não assinou. */
  protected reenviarCobranca(assinante: CommercialClientTermSignatoryResponse): void {
    this.acaoEmCurso.set(assinante.id);
    this.cdr.markForCheck();

    this.partnersHttpService
      .resendDealTermSignature(this.dealId, assinante.id)
      .pipe(
        first(),
        finalize(() => {
          this.acaoEmCurso.set(null);
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () =>
          this.toastHelperService.showToastSuccess(
            'Cobrança reenviada',
            `Um novo e-mail foi enviado para ${assinante.email}.`
          ),
        error: erro => this.avisarFalha('Não foi possível reenviar', erro),
      });
  }

  /** Quantos signatários ainda não assinaram — decide se o botão de lote aparece. */
  protected get pendentesDeAssinatura(): number {
    return this.aguardando.length;
  }

  /**
   * Reenvia o convite a todos os pendentes de uma vez.
   *
   * ⚠️ Convive com o reenvio individual, e não o substitui: cobrar cinco pessoas em cinco cliques
   * é trabalho que a tela deve poupar, mas cobrar **uma** continua sendo o que se quer quando só
   * falta ela.
   *
   * A mensagem diz **quantos** foram, e não "enviado com sucesso": um destinatário pode falhar
   * sem derrubar os outros, e "2 de 3" é a informação que permite agir.
   */
  protected reenviarParaTodos(): void {
    const pendentes = this.pendentesDeAssinatura;

    this.acaoEmCurso.set('todos');
    this.cdr.markForCheck();

    this.partnersHttpService
      .resendAllDealTermSignatures(this.dealId)
      .pipe(
        first(),
        finalize(() => {
          this.acaoEmCurso.set(null);
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: enviados =>
          this.toastHelperService.showToastSuccess(
            'Convites reenviados',
            `${enviados} de ${pendentes} ${pendentes === 1 ? 'convite' : 'convites'} reenviado${
              enviados === 1 ? '' : 's'
            }.`
          ),
        error: erro => this.avisarFalha('Não foi possível reenviar', erro),
      });
  }

  /** Recarrega o termo depois que a modal salva. */
  protected termoAlterado(): void {
    this.partnersHttpService
      .getDealTerm(this.dealId)
      .pipe(first())
      .subscribe(termo => {
        this.term.set(termo);
        this.cdr.markForCheck();
      });
  }

  /**
   * Mostra a mensagem do backend, e não um texto genérico.
   *
   * É ela que diz o que fazer — "sem assinante da Legatus configurado", "quem já assinou não pode
   * ser removido". O genérico fica só para falha técnica, onde não há o que dizer.
   */
  private avisarFalha(titulo: string, erro: { errors?: { message: string }[] }): void {
    this.toastHelperService.showToastError(
      titulo,
      erro?.errors?.length ? erro.errors[0].message : this.t(this.tk.General.RequestError)
    );
  }

  protected selectTab(tab: DealTab): void {
    this.updateUrlState({ stage: this.selectedStage(), tab });
  }

  /** Regra em `deal-tabs.ts` (card 3268): só na etapa Gates exibida, visão interna e `DFP_VIEW`. */
  protected get canSeeEconomicAnalysis(): boolean {
    return canSeeEconomicAnalysis(
      this.deal()?.hasEconomicAnalysis === true,
      this.visaoInterna,
      this.hasDfpPermission()
    );
  }

  /** Regra em `deal-tabs.ts` (card 3380): todas as etapas, só time interno, só com dado. */
  protected get canSeeKyc(): boolean {
    return canSeeKyc(this.visaoInterna, this.kycCompany() !== null, this.term());
  }

  /** Regra em `deal-tabs.ts` (card 3707): o deal tem negociação ligada, em qualquer etapa. */
  protected get canSeeBanks(): boolean {
    return this.bankJourney.unlocked();
  }

  /** Regra em `deal-tabs.ts` (card 3878): Briefing só para o time interno com a permissão, em todas as etapas. */
  protected get canSeeBriefing(): boolean {
    return canSeeBriefing(this.visaoInterna, this.canViewBriefing());
  }

  /** Aviso de Consentimento SCR da Visão geral (card 3380); `null` quando não há termo. */
  protected get consentNotice(): ConsentNotice | null {
    return consentNotice(this.term());
  }

  /** A aba realmente exibida: uma aba condicional pode sumir com ela selecionada (`deal-tabs.ts`). */
  protected get shownTab(): DealTab {
    return resolveShownTab(this.activeTab(), {
      economicAnalysis: this.canSeeEconomicAnalysis,
      kyc: this.canSeeKyc,
      banks: this.canSeeBanks,
      briefing: this.canSeeBriefing,
    });
  }

  /**
   * Consulta o cadastro da Receita para a aba KYC (card 3380), só para o time interno. Falha ou
   * cadastro vazio deixam `kycCompany` nulo: a aba depende só do termo assinado, e a etapa Registro
   * continua mostrando o erro no lugar dela.
   */
  private loadKycCompany(deal: PartnerDealResponse | null): void {
    this.kycCompany.set(null);
    if (!deal?.cnpj || deal.canDecide !== true) return;

    const cnpj = deal.cnpj;
    this.registryCache
      .obter(`${cnpj}|empresa`, () =>
        this.registryHttp.getEmpresa(cnpj).pipe(
          map(e => {
            if (!e) throw new ErrorResponse('Cadastro vazio', []);
            return e;
          })
        )
      )
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(company => {
        this.kycCompany.set(company);
        this.cdr.markForCheck();
      });
  }

  /**
   * As etapas do funil com o estado de cada uma.
   *
   * O estado vem do servidor, calculado sobre a linha do tempo (card 3189): etapa pulada ao arrastar
   * o deal fica pendente (relógio) em vez de concluída, e etapa já percorrida continua concluída
   * quando o deal volta — antes ela sumia do stepper e parecia que o histórico tinha resetado.
   *
   * ⚠️ A ordem sai de `etapasDoFunil` (o funil de quem vê). Sem a linha do tempo (falha ao
   * carregar), vale a régua antiga: concluída = vem antes da etapa atual.
   */
  protected get stepper(): StepperStage[] {
    const atual = this.deal()?.stage;
    // Sem as etapas internas para o parceiro (card 3088): Gates não entra na jornada dele.
    const etapas = this.etapasDoFunil;
    const indiceAtual = atual ? etapas.indexOf(atual) : -1;
    const doServidor = new Map((this.timeline()?.stages ?? []).map(e => [e.stage, e]));

    return etapas.map((stage, i) => {
      const progresso = doServidor.get(stage);
      const current = i === indiceAtual;
      return {
        stage,
        // Rótulo do parceiro ("NDA e Bacen", "Contrato enviado") fora da visão interna (card 3612).
        label: this.rotuloEtapa[stage],
        done: progresso
          ? progresso.progress === 'Done' && !current
          : indiceAtual >= 0 && i < indiceAtual,
        skipped: !current && progresso?.progress === 'Skipped',
        current,
        completedAt: progresso?.completedAt ?? null,
      };
    });
  }

  /** A etapa cujo conteúdo a Visão geral está mostrando. */
  protected get shownStage(): PartnerDealStage | null {
    const current = this.deal()?.stage ?? null;
    const selected = this.selectedStage();
    if (!selected || !current) return current;
    // A escolhida só vale se a pessoa poderia clicá-la no stepper. Uma decisão (rejeitar/reabrir/
    // pré-aprovar) pode ter voltado o deal para ANTES dela, e desde o card 3307 ela também chega pela
    // URL: etapa interna para o parceiro (`?stage=gates`), futura ou nunca percorrida caem na atual.
    const step = this.stepper.find(s => s.stage === selected);
    return step && this.canSelectStage(step) ? selected : current;
  }

  /**
   * Só etapas até a atual: uma etapa futura não tem o que mostrar. A pulada entra — é justamente
   * nela que a pessoa vai ver o que ficou pendente (o termo do Consentimento, por exemplo).
   */
  protected canSelectStage(passo: StepperStage): boolean {
    if (passo.current) return true;
    const etapas = this.etapasDoFunil;
    const atual = this.deal()?.stage;
    const antesDaAtual = !!atual && etapas.indexOf(passo.stage) < etapas.indexOf(atual);
    return antesDaAtual && (passo.done || passo.skipped);
  }

  protected selectStage(passo: StepperStage): void {
    if (!this.canSelectStage(passo)) return;
    // Clicar na etapa atual volta ao padrão, em vez de "fixar" a atual e deixar de acompanhar o
    // deal quando ele avançar.
    this.updateUrlState({ stage: passo.current ? null : passo.stage, tab: 'overview' });
  }

  /**
   * O que a etapa EXIBIDA apresenta na Visão geral quando ela ainda não tem tela própria
   * (Documentação em diante). Registro e Consentimento têm conteúdo próprio no `@switch` do
   * template — o dossiê da empresa e o termo —, e por isso não passam por aqui.
   *
   * Olha a etapa exibida, e não só a atual: quem revê uma etapa pelo stepper vê o texto DELA.
   */
  protected get conteudoDaEtapa(): { titulo: string; texto: string } | null {
    return conteudoDaEtapaDoFunil(this.shownStage, this.visaoInterna);
  }

  /** "Etapa 2 de 9" — a posição no funil, dita em número. */
  protected get posicaoNoFunil(): { atual: number; total: number } {
    const atual = this.deal()?.stage;
    const etapas = this.etapasDoFunil;
    const indice = atual ? etapas.indexOf(atual) : -1;
    return { atual: indice + 1, total: etapas.length };
  }

  /**
   * A legenda de uma etapa no stepper: quando ela terminou, ou o que se espera dela.
   *
   * A data de uma etapa concluída é a última vez que o deal **saiu** dela, calculada pelo servidor
   * (card 3189). Etapa sem registro fica sem legenda em vez de ganhar um texto inventado: o
   * histórico só passou a ser gravado em 23/09/2026, e deals anteriores não têm de onde tirar a data.
   */
  protected legendaDaEtapa(passo: StepperStage): { texto?: string; data?: string } {
    if (passo.current) {
      // Só o Consentimento termina com o termo: com o avanço automático (card 3281, T01) o deal
      // chega a Documentação já com o termo assinado, e a régua antiga marcava a etapa atual como
      // concluída.
      const consentimentoCumprido =
        passo.stage === 'Consentimento' && this.term()?.status === 'Assinado';
      // Deal rejeitado não está andando: a etapa atual é onde ele parou (crítica de design do
      // card 3789, deal negado pelo comitê; vale para qualquer rejeição).
      if (this.deal()?.rejectedAt) return { texto: 'Parou aqui' };
      return { texto: consentimentoCumprido ? 'Concluída' : 'Em andamento' };
    }

    if (passo.skipped) return { texto: 'Pendente' };
    if (!passo.done) return { texto: 'A seguir' };

    // A data sai como ISO e quem formata é o pipe `vaultDate` no template: o
    // `DateFormatterService.formatDate` devolve Observable, e resolvê-lo aqui obrigaria a um
    // subscribe por etapa a cada ciclo de render.
    if (passo.completedAt) return { data: passo.completedAt };
    // Sem o estado do servidor (linha do tempo não carregou): a primeira saída registrada.
    const saida = (this.timeline()?.entries ?? []).find(e => e.fromStage === passo.stage);
    return saida ? { data: saida.occurredAt } : {};
  }

  /** Iniciais para o avatar. Duas letras: a primeira e a do último nome. */
  protected iniciais(nome?: string | null): string {
    const partes = (nome ?? '').trim().split(/\s+/).filter(Boolean);
    if (!partes.length) return '?';
    if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
    return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
  }

  /** Quanto por cento das assinaturas já foi colhido — largura da barra de progresso. */
  protected get progressoPercentual(): number {
    const { assinaram, total } = this.progressoDeAssinaturas;
    return total ? Math.round((assinaram / total) * 100) : 0;
  }

  /**
   * Quanto tempo o termo levou entre o envio e a última assinatura.
   *
   * ⚠️ Calculado com o `DateFormatterService` — `new Date()` é proibido em todo o projeto fora
   * dele. Enquanto não fechou, a resposta é "Em andamento" e não uma conta contra o relógio:
   * um número que cresce sozinho não é duração, é cronômetro.
   */
  protected get duracaoDoTermo(): string {
    const termo = this.term();
    if (!termo?.requestedAt) return '—';
    if (!termo.signedAt) return 'Em andamento';

    const inicio = this.dateFormatterService.parseDate(termo.requestedAt);
    const fim = this.dateFormatterService.parseDate(termo.signedAt);
    if (!inicio || !fim) return '—';

    const minutos = Math.max(0, Math.round((fim.getTime() - inicio.getTime()) / 60000));
    if (minutos < 60) return minutos <= 1 ? '1 min' : `${minutos} min`;

    const horas = Math.floor(minutos / 60);
    if (horas < 24) return horas === 1 ? '1 hora' : `${horas} horas`;

    const dias = Math.floor(horas / 24);
    return dias === 1 ? '1 dia' : `${dias} dias`;
  }

  /** CNPJ copiado há pouco — troca o ícone por um "check" temporário. */
  protected readonly cnpjCopiado = signal(false);

  /**
   * Copia o CNPJ para a área de transferência.
   *
   * O CNPJ é o que se cola em consulta externa, e relê-lo do dígito é onde o erro entra. Falha
   * de `clipboard` (contexto sem permissão) é engolida de propósito: não há o que o usuário
   * faça a respeito, e um toast de erro por um atalho seria ruído.
   */
  protected copiarCnpj(): void {
    const cnpj = this.deal()?.cnpj;
    if (!cnpj) return;

    navigator.clipboard
      ?.writeText(cnpj)
      .then(() => {
        this.cnpjCopiado.set(true);
        this.cdr.markForCheck();
        setTimeout(() => {
          this.cnpjCopiado.set(false);
          this.cdr.markForCheck();
        }, 1500);
      })
      .catch(() => undefined);
  }

  /** O que a situação do deal diz, em uma frase. Régua em `deal-presenter`. */
  protected get situacao(): SituacaoDoDeal {
    return resolverSituacao(this.deal(), this.visaoInterna);
  }

  /**
   * Se quem está vendo é do time interno — e portanto enxerga as etapas internas (Gates, card 3088).
   *
   * Sai do `canDecide` do servidor, que é exatamente "usuário interno sem parceiro": a mesma régua
   * que o backend usa para decidir se projeta a etapa. Sem deal carregado, vale a visão do parceiro.
   */
  protected get visaoInterna(): boolean {
    return this.deal()?.canDecide === true;
  }

  /** As etapas do funil para quem está vendo: sem as internas para o parceiro (card 3088). */
  protected get etapasDoFunil(): PartnerDealStage[] {
    return partnerDealStagesFor(this.visaoInterna);
  }

  /**
   * Defesa em profundidade do card 3088: o backend já devolve ao parceiro a etapa projetada
   * (Gates → Documentação), mas a tela não confia nisso para nunca desenhar uma etapa interna.
   */
  private projetarDealParaQuemVe(deal: PartnerDealResponse | null): PartnerDealResponse | null {
    if (!deal || deal.canDecide) return deal;
    const stage = toPartnerVisibleStage(deal.stage);
    return stage === deal.stage ? deal : { ...deal, stage };
  }

  /**
   * Mesma defesa para a linha do tempo: para o parceiro, troca etapa interna pela visível, some
   * com a mudança que vira "Documentação → Documentação" e tira o acumulado das etapas internas.
   * O cálculo certo (tempo de Gates somado em Documentação) é do backend.
   */
  private projetarLinhaDoTempoParaQuemVe(
    timeline: PartnerDealTimelineResponse | null,
    deal: PartnerDealResponse | null
  ): PartnerDealTimelineResponse | null {
    if (!timeline || deal?.canDecide) return timeline;

    const entries = (timeline.entries ?? [])
      .map(e => ({
        ...e,
        fromStage: e.fromStage ? toPartnerVisibleStage(e.fromStage) : e.fromStage,
        toStage: e.toStage ? toPartnerVisibleStage(e.toStage) : e.toStage,
      }))
      .filter(
        e =>
          !(e.eventType === 'StageChanged' && e.fromStage && e.toStage && e.fromStage === e.toStage)
      );

    return {
      ...timeline,
      // Autoria é auditoria da Legatus (card 3189): o servidor já não manda para o parceiro, e a
      // tela não mostra mesmo que venha.
      entries: entries.map(e => ({ ...e, performedBy: null })),
      stageDurations: (timeline.stageDurations ?? []).filter(
        d => !PARTNER_DEAL_INTERNAL_STAGES.has(d.stage)
      ),
      stages: (timeline.stages ?? []).filter(e => !PARTNER_DEAL_INTERNAL_STAGES.has(e.stage)),
    };
  }

  /** A pendência de quem está olhando — a faixa "Depende de você". */
  protected get dependeDeVoce(): string | null {
    return resolverDependeDeVoce(this.deal(), this.term()?.status, this.termoEmAndamento);
  }

  /** A etapa seguinte no funil, ou `null` quando o deal já está na última. */
  protected get proximaEtapa(): PartnerDealStage | null {
    return resolverProximaEtapa(this.deal()?.stage, this.etapasDoFunil);
  }

  /**
   * O cartão do alto da lateral: em que pé está o deal, e o botão que resolve.
   *
   * A régua mora em `deal-presenter.resolverProximoPasso`; aqui só se reúne o contexto. As
   * permissões entram como booleano **já resolvido** — quem decide isso é o servidor, e o
   * presenter não pode recalcular uma régua de autorização.
   */
  protected get proximoPasso(): ProximoPasso | null {
    const deal = this.deal();
    if (!deal) return null;

    return resolverProximoPasso({
      deal,
      statusDoTermo: this.term()?.status,
      termoEmAndamento: this.termoEmAndamento,
      pendentesDeAssinatura: this.pendentesDeAssinatura,
      podeDecidir: this.podeDecidir,
      podeAvancarEtapa: this.podeAvancarEtapa,
      podeReabrir: this.podeReabrir,
      rejeicao: this.rejeicaoAtual,
      rejeitadoEm: this.dateFormatterService.formatDateTimeMinutesInstant(deal.rejectedAt),
      committeeStep: resolveCommitteeNextStep(
        deal.stage,
        this.visaoInterna,
        this.canSubmitToCommittee(),
        this.committeeView()
      ),
      offerCommitteeSubmit: shouldOfferCommitteeSubmit(
        deal.stage,
        this.visaoInterna,
        this.committeeView()
      ),
    });
  }

  /** "Enviar ao comitê" do cartão próximo passo (card 3789): a modal lê o comitê e envia. */
  protected openCommitteeSubmit(): void {
    const deal = this.deal();
    if (!deal) return;
    this.modalCommitteeSubmit?.open(deal.id, deal.corporateName);
  }

  /**
   * O deal mudou pelo comitê: relê o deal e a linha do tempo.
   *
   * - Envio pela modal do próximo passo (`fromPanel = false`): de Análise o deal vai para Comitê, e a
   *   tela passa a mostrar a etapa atual.
   * - Decisão no painel (`fromPanel = true`): aprovado vai para Cotação; a tela continua na etapa
   *   Comitê, para quem votou ver o comitê encerrado (o stepper leva à etapa nova).
   */
  protected onCommitteeDealChanged(
    view: PartnerDealCommitteeViewResponse | null = null,
    fromPanel = false
  ): void {
    if (view) this.committeeView.set(view);
    this.updateUrlState({ stage: fromPanel ? 'Comite' : null, tab: this.shownTab }, true);
    this.loadDeal(true);
  }

  /**
   * Move o deal para a etapa seguinte.
   *
   * Pede confirmação porque a etapa é o que o board inteiro mostra, e voltar exige arrastar o
   * card de volta — não há desfazer aqui.
   *
   * ⚠️ **Não serve para sair de Registro**, nem para o time interno: dali o caminho é
   * {@link preAprovar}, que registra a decisão, avisa o parceiro e escolhe o destino. A guarda
   * repete a régua do cartão porque o método é público para o template e não pode depender de
   * quem o chama.
   */
  protected avancarEtapa(): void {
    const deal = this.deal();
    const seguinte = this.proximaEtapa;
    if (!deal || !seguinte || !this.podeAvancarEtapa) return;
    if (deal.stage === 'Registro') return;

    this.confirmationService
      .confirm(
        `Avançar para ${PARTNER_DEAL_STAGE_LABELS[seguinte]}?`,
        `${deal.corporateName} sai de ${PARTNER_DEAL_STAGE_LABELS[deal.stage]} e a mudança aparece no board.`
      )
      .pipe(first())
      .subscribe(confirmado => {
        if (!confirmado) return;

        this.acaoEmCurso.set('avancar');
        this.cdr.markForCheck();

        this.partnersHttpService
          .updateDealStage(deal.id, seguinte)
          .pipe(
            first(),
            finalize(() => {
              this.acaoEmCurso.set(null);
              this.cdr.markForCheck();
            })
          )
          .subscribe({
            next: () => {
              this.toastHelperService.showToastSuccess(
                `Deal movido para ${PARTNER_DEAL_STAGE_LABELS[seguinte]}.`
              );
              // A tela passa a mostrar a etapa nova, e não uma etapa revista antes do avanço.
              this.updateUrlState({ stage: null, tab: this.shownTab }, true);
              this.loadDeal();
            },
            error: erro => this.avisarFalha('Não foi possível avançar a etapa', erro),
          });
      });
  }

  /**
   * De quem é o deal, no cabeçalho — **o parceiro na visão interna, a pessoa na do parceiro**.
   *
   * Sem `partnerId` é deal da própria Legatus: não há ninguém a nomear, e o rótulo diz isso.
   * Antes essa linha exibia "Originado pelo parceiro" também aí, o que era simplesmente falso.
   *
   * `partnerName` só vem preenchido para o **usuário interno**: no board do parceiro todo
   * deal é dele, e o backend nem consulta o nome (card 2913). Já na visão da Legatus, saber
   * que veio *de um* parceiro não ajuda; saber **de qual** ajuda, e é a mesma decisão já
   * tomada no rodapé do card do board (14/09/2026).
   *
   * ⚠️ **Na visão do parceiro quem aparece é a PESSOA** (`registeredByName`), e não a frase
   * genérica: dizer a ele "originado pelo parceiro" não distingue nada, porque todo deal do
   * board dele é do parceiro dele. O que ele precisa saber é **quem da equipe trouxe** — o
   * master enxerga os deals de todos os usuários (card 2929).
   *
   * O fallback para o rótulo genérico cobre o deal cujo autor não resolveu (registro antigo
   * sem autor, ou usuário que saiu da base): melhor o texto antigo do que um vazio.
   *
   * ⚠️ **Nada aqui depende da etapa.** Mover o deal de coluna não muda origem nem autoria —
   * o `UpdateStageAsync` toca só o `Stage`. Se o rótulo parecer ter mudado depois de um
   * arrasto, o que mudou foi a VISÃO (interna × parceiro), não o dado.
   */
  protected get origemDoDeal(): string {
    return resolverOrigemDoDeal(this.deal());
  }

  /** Prazo da captação e flat fee estimado (card 3683). Régua em `deal-presenter`. */
  protected get dealTerm(): DealTermSummary {
    return resolveDealTerm(this.deal());
  }

  /** Dias restantes da reserva do CNPJ. Nulo quando a reserva já é definitiva. */
  protected get diasDeReserva(): number | null {
    const expira = this.deal()?.reservationExpiresAt;
    if (!expira) return null;

    const hoje = this.dateFormatterService.getTodayDate();
    const fim = this.dateFormatterService.parseDate(expira);
    if (!fim) return null;

    const dias = Math.ceil((fim.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
    return dias > 0 ? dias : 0;
  }

  /** Texto do acontecimento na linha do tempo. */
  protected descreverEvento(entrada: PartnerDealTimelineEntryResponse): string {
    return descreverEventoDoDeal(entrada, this.reasonLabels, this.visaoInterna);
  }

  /** Ícone do acontecimento na linha do tempo. */
  protected iconeDoEvento(entrada: PartnerDealTimelineEntryResponse): string {
    return iconeDoEventoDoDeal(entrada);
  }

  /** Converte a duração do .NET (`d.hh:mm:ss`) em algo legível. */
  protected formatarDuracao(valor?: string | null): string {
    return formatarDuracaoDeEtapa(valor);
  }

  /** Rótulos das etapas para quem está vendo: o parceiro lê "NDA e Bacen" e "Contrato enviado" (card 3612). */
  protected get rotuloEtapa(): Record<PartnerDealStage, string> {
    return this.visaoInterna ? PARTNER_DEAL_STAGE_LABELS : PARTNER_VIEW_STAGE_LABELS;
  }

  /**
   * Assinantes de um lado, com quem já assinou primeiro.
   *
   * A ordem não é estética: quem olha essa lista quer saber **de quem se está esperando**, e
   * empurrar os pendentes para o fim faria justamente eles sumirem numa lista longa. Por isso os
   * assinados sobem e os pendentes ficam visíveis logo abaixo, agrupados.
   */
  protected assinantesDoLado(lado: 'Cliente' | 'Legatus'): CommercialClientTermSignatoryResponse[] {
    return (this.term()?.signatories ?? [])
      .filter(s => s.side === lado)
      .sort((a, b) => Number(!!b.signedAt) - Number(!!a.signedAt));
  }

  /** Quantos já assinaram, de quantos. */
  protected get progressoDeAssinaturas(): { assinaram: number; total: number } {
    const assinantes = this.term()?.signatories ?? [];
    return {
      assinaram: assinantes.filter(s => !!s.signedAt).length,
      total: assinantes.length,
    };
  }

  /**
   * Quem ainda não assinou, para a frase de "aguardando".
   *
   * ⚠️ Sai do `signedAt` de cada assinante, e **não** do status do termo. Os dois vêm do mesmo
   * lugar, mas o status é um resumo: com ele não dá para dizer *de quem* se está esperando, que é
   * exatamente a pergunta de quem abre esta tela.
   */
  protected get aguardando(): CommercialClientTermSignatoryResponse[] {
    return (this.term()?.signatories ?? []).filter(s => !s.signedAt);
  }
}
